import React, { useEffect, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getWatchlist, removeWatchlist } from "../lib/api";
import { useCurrency } from "../lib/currency";
import { X, Star } from "lucide-react";
import { toast } from "sonner";

export default function Watchlist() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const nav = useNavigate();
    const { format } = useCurrency();

    const load = useCallback(() => {
        setLoading(true);
        getWatchlist().then(setItems).catch(() => setItems([])).finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);

    const remove = async (sym, e) => {
        e.stopPropagation();
        try {
            await removeWatchlist(sym);
            toast.success(`${sym} rimosso dalla watchlist`);
            load();
        } catch (err) {
            toast.error("Errore: " + err.message);
        }
    };

    return (
        <div data-testid="watchlist-page" className="px-4 py-6 max-w-[1600px] mx-auto fade-up">
            <div className="mb-6">
                <div className="text-[10px] text-mute tracking-[0.2em] mb-1">PERSONAL WATCHLIST</div>
                <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight brand-gradient-text">
                    La tua watchlist
                </h1>
            </div>

            {loading && <div className="text-mute text-sm">Caricamento…</div>}
            {!loading && items.length === 0 && (
                <div className="border border-soft bg-elev p-12 text-center">
                    <Star size={32} className="mx-auto text-mute mb-3" />
                    <div className="text-dim text-sm mb-2">La tua watchlist è vuota</div>
                    <div className="text-mute text-xs">Apri un asset dalla Dashboard e clicca "WATCHLIST" per aggiungerlo.</div>
                </div>
            )}
            {!loading && items.length > 0 && (
                <div className="border border-soft bg-elev">
                    <div className="grid grid-cols-12 px-4 py-2.5 text-[10px] tracking-widest text-mute border-b border-soft uppercase">
                        <div className="col-span-3">Simbolo</div>
                        <div className="col-span-4">Nome</div>
                        <div className="col-span-2 text-right">Prezzo</div>
                        <div className="col-span-2 text-right">24h %</div>
                        <div className="col-span-1"></div>
                    </div>
                    {items.map(it => {
                        const up = (it.change_pct || 0) >= 0;
                        return (
                            <button
                                key={it.symbol}
                                data-testid={`watchlist-row-${it.symbol}`}
                                onClick={() => nav(`/asset/${encodeURIComponent(it.symbol)}`)}
                                className="w-full grid grid-cols-12 px-4 py-3 text-sm border-b border-soft items-center hover:bg-elev2 text-left"
                            >
                                <div className="col-span-3 font-semibold text-amber">{it.symbol}</div>
                                <div className="col-span-4 text-dim text-xs truncate">{it.name}</div>
                                <div className="col-span-2 text-right tnum">{format(it.price, { decimals: 4 })}</div>
                                <div className={`col-span-2 text-right tnum ${up ? "text-green-up" : "text-red-down"}`}>
                                    {up ? "+" : ""}{(it.change_pct || 0).toFixed(2)}%
                                </div>
                                <div className="col-span-1 flex justify-end">
                                    <span
                                        data-testid={`remove-${it.symbol}`}
                                        onClick={(e) => remove(it.symbol, e)}
                                        className="text-mute hover:text-red-down p-1 cursor-pointer"
                                    >
                                        <X size={14} />
                                    </span>
                                </div>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
